import React from "react";
import {
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
  CircularProgress,
} from "@mui/material";

import { PermissionType } from "amnShared/Permissions/IPermissions";
import usePermissions from "amnShared/Permissions/usePermissions";
import useApproval from "amnPos/Approvals/useApproval";
import { GetNeededApproval } from "amnPos/Approvals/approvalHelper";
import { IAskSupervisorApproval } from "amnPos/Approvals/IApprovals";

import {
  AmeenButtonVariant,
  AmeenButtonSize,
} from "../../theme/components/button.theme";

export type ButtonVariant = AmeenButtonVariant;
export type ButtonSize = AmeenButtonSize;

export interface ButtonProps
  extends Omit<MuiButtonProps, "variant" | "size" | "onClick"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  loading?: boolean;
  square?: boolean;
  permission?: PermissionType;
  permissionAdditionalInfo?: IAskSupervisorApproval;

  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

export const Button: React.FC<ButtonProps> = ({
  variant = "primary",
  size = "medium",
  fullWidth = false,
  loading = false,
  disabled = false,
  square = false,
  children,
  startIcon,
  endIcon,
  sx,
  permission = PermissionType.NONE,
  permissionAdditionalInfo = {},
  onClick,
  ...props
}) => {
  const { canEmployee } = usePermissions();
  const { AskSupervisorApproval } = useApproval();

  return (
    <MuiButton
      {...props}
      variant={variant as MuiButtonProps["variant"]}
      size={size as MuiButtonProps["size"]}
      fullWidth={fullWidth}
      disabled={disabled || loading}
      startIcon={startIcon}
      endIcon={endIcon}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
        }
      }}
      sx={{
        ...(square && {
          aspectRatio: "1 / 1",
          minWidth: "auto",
          padding:
            size === "small"
              ? "6px"
              : size === "large"
                ? "12px"
                : "8px",
        }),
        ...sx,
      }}
      onClick={(e) => {
        e.stopPropagation();

        if (permission === PermissionType.NONE) {
          onClick?.(e);
          return;
        }

        const perm = canEmployee(permission);

        if (perm.response) {
          onClick?.(e);
          return;
        }

        const neededApproval = GetNeededApproval(perm.error);

        AskSupervisorApproval(
          neededApproval,
          () => {
            onClick?.(e);
          },
          () => {},
          permissionAdditionalInfo,
        );
      }}
    >
      {loading ? <CircularProgress size={20} /> : children}
    </MuiButton>
  );
};

export default Button;